import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { CalendarDate, getLocalTimeZone, today as todayCD, } from '@internationalized/date';
import { useDispatch, useSelector } from 'react-redux';
import CommonDrawerLayout from '../../../common_components/CommonDrawerLayout';
import AppButton from '../../../common_components/AppButton';
import { NumberInput, Dropdown, FormRow, TextInput, } from '../../../common_components/FormInputs';
import CustomDatePicker from '../../../common_components/daterangepicker/CustomDatePicker';
import { createReportThunk, updateReportThunk, fetchVisibleClients, selectVisibleClients, selectVisibleClientsLoading, selectSaving, } from '../../../redux/metaAdAuditSlice';
import { toast } from '../../../common_components/Toast/ToastUtils';
// helpers ────────────────────────────────────────────────────────────────
function toCalendarDate(iso) {
    if (!iso)
        return todayCD(getLocalTimeZone());
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new CalendarDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}
function calendarDateToIso(cd) {
    const mm = String(cd.month).padStart(2, '0');
    const dd = String(cd.day).padStart(2, '0');
    return `${cd.year}-${mm}-${dd}`;
}
function defaultPeriod() {
    const t = todayCD(getLocalTimeZone());
    const end = t.subtract({ days: 1 });
    const start = end.subtract({ days: 365 });
    return { start, end };
}
const AddReportDrawer = ({ open, onClose, onSuccess, editData, }) => {
    const dispatch = useDispatch();
    const visibleClients = useSelector(selectVisibleClients);
    const visibleClientsLoading = useSelector(selectVisibleClientsLoading);
    const saving = useSelector(selectSaving);
    const isEdit = !!editData;
    const [reportName, setReportName] = useState('');
    const [clientId, setClientId] = useState('');
    const [periodStart, setPeriodStart] = useState(defaultPeriod().start);
    const [periodEnd, setPeriodEnd] = useState(defaultPeriod().end);
    const [threshold, setThreshold] = useState('190');
    // NEW (Batch 9) — benchmark window in days. One of 7, 14, 30, 90.
    // Default 30 on new reports.
    const [benchmarkWindowDays, setBenchmarkWindowDays] = useState('30');
    const [errors, setErrors] = useState({});
    useEffect(() => {
        if (!open)
            return;
        setErrors({});
        if (isEdit && editData) {
            setReportName(editData.label || '');
            setClientId(String(editData.client_id));
            setPeriodStart(toCalendarDate(editData.period_start));
            setPeriodEnd(toCalendarDate(editData.period_end));
            setThreshold(String(editData.threshold ?? 190));
            setBenchmarkWindowDays(String(editData.benchmark_window_days ?? 30));
        }
        else {
            const d = defaultPeriod();
            setReportName('');
            setClientId('');
            setPeriodStart(d.start);
            setPeriodEnd(d.end);
            setThreshold('190');
            setBenchmarkWindowDays('30');
        }
        if (!isEdit) {
            dispatch(fetchVisibleClients());
        }
    }, [open, isEdit, editData, dispatch]);
    const clientOptions = useMemo(() => (visibleClients || []).map((c) => ({
        value: String(c._id),
        label: c.name,
    })), [visibleClients]);
    const editModeClientOption = useMemo(() => {
        if (!isEdit || !editData)
            return null;
        return [{ value: String(editData.client_id), label: editData.client_name }];
    }, [isEdit, editData]);
    // Auto-suggest a default report name when client is set in Add mode
    useEffect(() => {
        if (isEdit)
            return;
        if (reportName.trim())
            return; // user has typed something — don't overwrite
        if (!clientId)
            return;
        const c = visibleClients.find((x) => String(x._id) === clientId);
        if (!c)
            return;
        setReportName(c.name);
    }, [clientId, visibleClients, isEdit]);
    const validate = () => {
        const e = {};
        const trimmedName = reportName.trim();
        if (trimmedName.length > 200) {
            e.reportName = 'Report Name must be 200 characters or fewer.';
        }
        if (!isEdit && !clientId) {
            e.clientId = 'Please select a client.';
        }
        if (!periodStart || !periodEnd) {
            e.period = 'Please select a date range.';
        }
        else if (periodEnd.compare(periodStart) <= 0) {
            e.period = 'Period end must be after period start.';
        }
        const t = Number(threshold);
        if (!Number.isFinite(t) || t <= 0) {
            e.threshold = 'Threshold must be a positive number.';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };
    const handleSave = async () => {
        if (!validate())
            return;
        const pStart = calendarDateToIso(periodStart);
        const pEnd = calendarDateToIso(periodEnd);
        const thresholdNum = Number(threshold);
        let trimmedName = reportName.trim();
        if (!trimmedName) {
            if (isEdit && editData) {
                trimmedName = editData.label;
            }
            else {
                const c = visibleClients.find((x) => String(x._id) === clientId);
                trimmedName = c ? c.name : 'Untitled Report';
            }
        }
        try {
            if (isEdit && editData) {
                const result = await dispatch(updateReportThunk({
                    reportId: editData.report_id,
                    payload: {
                        label: trimmedName,
                        period_start: pStart,
                        period_end: pEnd,
                        threshold: thresholdNum,
                        benchmark_window_days: Number(benchmarkWindowDays),
                    },
                })).unwrap();
                toast.success(result.queued
                    ? result.message || 'Report update is running in the background.'
                    : result.message || 'Report updated');
            }
            else {
                const result = await dispatch(createReportThunk({
                    label: trimmedName,
                    client_id: clientId,
                    period_start: pStart,
                    period_end: pEnd,
                    threshold: thresholdNum,
                    benchmark_window_days: Number(benchmarkWindowDays),
                })).unwrap();
                toast.success(result.queued
                    ? result.message ||
                        'Report is being generated in the background. It will appear in the list when ready.'
                    : result.message || 'Report generated');
            }
            onSuccess();
            onClose();
        }
        catch (err) {
            toast.error(typeof err === 'string' ? err : 'Save failed');
        }
    };
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: isEdit ? 'Edit Report' : 'Add Report', width: "md", headerActions: _jsx(AppButton, { variant: "contained", onClick: handleSave, disabled: saving, sx: {
                borderRadius: '8px',
                px: 3,
                bgcolor: '#FF008E',
                '&:hover': { bgcolor: '#E6007A' },
                textTransform: 'none',
                fontWeight: 600,
            }, children: saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Generate Report' }), children: _jsxs(Box, { sx: { p: 1, bgcolor: '#F9FAFB', minHeight: '100%' }, children: [isEdit && (_jsx(Box, { sx: {
                        bgcolor: '#FFF8F0',
                        border: '1px solid #F9DBAF',
                        borderRadius: '8px',
                        p: 1,
                        mb: 1,
                    }, children: _jsx(Typography, { sx: { color: '#93370D', fontWeight: 500 }, className: 'all-Font-Sizes', children: "You cannot change the client on an existing report. To audit a different client, create a new report." }) })), _jsxs(Box, { sx: {
                        bgcolor: 'white',
                        border: '1px solid #EAECF0',
                        borderRadius: '8px',
                        p: 1,
                    }, children: [_jsxs(Box, { sx: { mb: 2 }, children: [_jsx(Typography, { sx: { color: '#FF008E', fontWeight: 600, mb: 0.5 }, className: 'all-Font-Sizes', children: "Report Details" }), _jsx(Divider, { sx: { borderColor: '#EAECF0' } })] }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [_jsxs(FormRow, { columns: 2, gap: 24, children: [_jsx(Dropdown, { label: "Client", required: true, value: clientId, onChange: (v) => setClientId(v), options: isEdit && editModeClientOption
                                                ? editModeClientOption
                                                : clientOptions, placeholder: visibleClientsLoading
                                                ? 'Loading clients…'
                                                : 'Select a client', disabled: isEdit || visibleClientsLoading, searchable: true, error: !!errors.clientId, errorMessage: errors.clientId }), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', width: '100%' }, children: [_jsxs(Typography, { sx: {
                                                        color: '#344054',
                                                        mb: 0.75,
                                                        fontWeight: 500,
                                                        lineHeight: 1.4,
                                                    }, className: 'all-Font-Sizes', children: ["Duration ", _jsx("span", { style: { color: '#D92D20' }, children: "*" })] }), _jsx(Box, { className: "all-Font-Sizes", sx: {
                                                        border: '1px solid #D0D5DD',
                                                        borderRadius: '8px',
                                                        height: '44px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                        bgcolor: 'white',
                                                        overflow: 'hidden',
                                                        '&:hover': { borderColor: '#98A2B3' },
                                                        '&:focus-within': {
                                                            borderColor: '#FF008E',
                                                            boxShadow: '0 0 0 3px rgba(255, 0, 142, 0.1)',
                                                        },
                                                        '& .custom-date-picker-wrapper': {
                                                            width: '100%',
                                                            display: 'block',
                                                        },
                                                        '& .date-picker-trigger-container': {
                                                            width: '100%',
                                                            gap: 0,
                                                        },
                                                        '& .date-picker-trigger-field': {
                                                            border: 'none !important',
                                                            bgcolor: 'transparent !important',
                                                            borderRadius: '0 !important',
                                                            boxShadow: 'none !important',
                                                            width: '100%',
                                                            height: '42px',
                                                            maxWidth: 'unset',
                                                            minWidth: 'unset',
                                                            padding: '0 12px !important',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        },
                                                        '& .date-display-text': {
                                                            color: '#101828 !important',
                                                        },
                                                        '& .calendar-icon': {
                                                            color: '#667085 !important',
                                                            mr: 1,
                                                        },
                                                    }, children: _jsx(CustomDatePicker, { value: { start: periodStart, end: periodEnd }, onChange: (range) => {
                                                            if (range) {
                                                                setPeriodStart(range.start);
                                                                setPeriodEnd(range.end);
                                                            }
                                                        }, comparison: false, maxPastYears: 5, size: "compact" }) }), errors.period && (_jsx(Typography, { sx: { color: '#D92D20', mt: 0.5 }, className: "all-Font-Sizes", children: errors.period }))] })] }), _jsxs(FormRow, { columns: 2, gap: 24, children: [_jsx(NumberInput, { label: "Threshold", value: threshold, onChange: (e) => setThreshold(e?.target?.value ?? ''), placeholder: "190", error: !!errors.threshold, errorMessage: errors.threshold }), _jsx(TextInput, { label: "Report Name", value: reportName, onChange: (e) => setReportName(e?.target?.value ?? ''), placeholder: "e.g. Client Name \u2014 Jan to Dec 2025", error: !!errors.reportName, errorMessage: errors.reportName })] }), _jsxs(FormRow, { columns: 2, gap: 24, children: [_jsx(Dropdown, { label: "Benchmark Duration", required: true, value: benchmarkWindowDays, onChange: (v) => setBenchmarkWindowDays(String(v ?? '30')), options: [
                                                { value: '7', label: '7 days' },
                                                { value: '14', label: '14 days' },
                                                { value: '30', label: '30 days' },
                                                { value: '60', label: '60 days' },
                                                { value: '90', label: '90 days' },
                                            ], placeholder: "Select benchmark duration", sortByLabel: false }), _jsx(Box, {})] })] })] })] }) }));
};
export default AddReportDrawer;
